import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useSitePhase } from "@/lib/useSitePhase";

const fmtEur = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export const TicketAvailabilityBanner = () => {
  const [av, setAv] = useState(null);
  const [tiers, setTiers] = useState([]);
  const phase = useSitePhase();
  const phaseOpen = !phase || phase.sales_open;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [a, t] = await Promise.all([
          api.get("/ticket-availability"),
          api.get("/tickets"),
        ]);
        if (!cancelled) {
          setAv(a.data);
          setTiers(t.data || []);
        }
      } catch {
        /* banner is optional */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!av || !phaseOpen) return null;

  const online = tiers.filter((x) => !x.onsite_only);
  const minPrice = online.length ? Math.min(...online.map((x) => x.price)) : null;
  const onsite = tiers.find((x) => x.onsite_only);
  const percent = av.unlimited ? 0 : Math.min(100, Math.round(av.percent_sold));

  return (
    <section
      data-testid="ticket-availability-banner"
      className="relative bg-[#0e0e0e] border-t border-white/5 px-6 md:px-12 py-16 md:py-20"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-start">
        <div className="md:col-span-7">
          <div className={`eyebrow mb-4 ${av.sold_out ? "text-red-500" : "gold-text"}`}>
            {av.sold_out ? "Ausverkauft" : "Ticket-Verfügbarkeit"}
          </div>
          {av.sold_out ? (
            <h2
              data-testid="ticket-availability-text"
              className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1] text-red-500"
            >
              Online-Tickets ausverkauft.
            </h2>
          ) : (
            <h2
              data-testid="ticket-availability-text"
              className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1] text-white"
            >
              {av.unlimited ? (
                <>Tickets jetzt sichern.</>
              ) : (
                <>
                  Nur noch <span className="gold-text">{av.remaining.toLocaleString("de-DE")}</span> von{" "}
                  {av.total.toLocaleString("de-DE")} Plätzen.
                </>
              )}
            </h2>
          )}

          {!av.unlimited && (
            <div className="mt-8 max-w-xl">
              <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
                <div
                  data-testid="ticket-availability-progress"
                  className={`h-full rounded-full transition-[width] duration-1000 ${av.sold_out ? "bg-red-500" : "bg-[#FFCC51]"}`}
                  style={{ width: `${percent}%` }}
                />
              </div>
              <div className="mt-3 flex justify-between eyebrow text-zinc-500">
                <span>{percent} % verkauft</span>
                <span>
                  {av.sold} von {av.total} Besuchern
                </span>
              </div>
            </div>
          )}

          <p className="mt-8 text-zinc-400 max-w-xl">
            {av.sold_out
              ? "Alle Online-Tickets sind vergriffen. Restkarten gibt es — solange verfügbar — nur noch an der Tageskasse vor Ort."
              : "Das Besucher-Kontingent ist begrenzt (ein Familienticket zählt als 5 Besucher). Sichere dir deinen Platz im Vorverkauf und spare dir die Warteschlange an der Tageskasse."}
          </p>

          <Link
            to="/tickets"
            data-testid="ticket-availability-cta"
            className={`${av.sold_out ? "ghost-button" : "gold-button"} mt-8`}
          >
            {av.sold_out ? "Ticket-Infos ansehen" : "Ticket sichern"} <ArrowRight size={18} />
          </Link>
        </div>

        <div className="md:col-span-5 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-4 w-full">
          {minPrice != null && (
            <div data-testid="banner-hint-price" className="tile tile-gold">
              <div className="eyebrow text-zinc-500 mb-2">Online-Ticket ab</div>
              <div className="font-display font-black text-3xl text-white">{fmtEur(minPrice)}</div>
              <p className="text-sm text-zinc-400 mt-2">Direkt als PDF mit QR-Code per E-Mail.</p>
            </div>
          )}
          {onsite && (
            <div data-testid="banner-hint-onsite" className="tile tile-gold">
              <div className="eyebrow text-zinc-500 mb-2 flex items-center gap-1.5">
                <MapPin size={13} className="text-[#FFCC51]" /> Tageskasse vor Ort
              </div>
              <div className="font-display font-black text-3xl text-white">{fmtEur(onsite.price)}</div>
              <p className="text-sm text-zinc-400 mt-2">Ab 11:00 Uhr · nur vor Ort, solange verfügbar.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
